import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import {
  getSyncConflicts,
  getSyncStatus,
  resolveSyncConflict,
  TSyncConflict,
  TSyncRun,
} from '../../../requests/opsSync';
import {
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  PageBody,
  PageHeader,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
} from '../ui';

/** partial = the run finished, but some objects did not refresh. */
const RUN_TONE: Record<TSyncRun['status'], 'good' | 'warn' | 'bad' | 'info'> = {
  ok: 'good',
  partial: 'warn',
  failed: 'bad',
  running: 'info',
};

const when = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const show = (v: unknown) =>
  v === null || v === undefined || v === '' ? (
    <span className="text-gray-two">(empty)</span>
  ) : typeof v === 'object' ? (
    JSON.stringify(v)
  ) : (
    String(v)
  );

const Conflict: React.FC<{
  c: TSyncConflict;
  onResolve: (choice: 'portal' | 'salesforce') => void;
  busy: boolean;
}> = ({ c, onResolve, busy }) => (
  <div className="border-b border-white-three px-5 py-4 last:border-b-0">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <div>
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-two">
          {c.object}
        </span>
        <span className="ml-2 font-medium text-gray-five">{c.label}</span>
        <span className="ml-2 text-xs text-gray-bold">{c.targetId}</span>
      </div>
      <div className="text-xs text-gray-bold">
        portal edit by {c.portalEditor ?? '—'} · {when(c.portalEditedAt)} ·
        Salesforce changed {when(c.salesforceStamp)}
      </div>
    </div>
    <table className="mt-3 w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-two">
          <th className="py-1 pr-4">Field</th>
          <th className="py-1 pr-4">Portal has</th>
          <th className="py-1">Salesforce has</th>
        </tr>
      </thead>
      <tbody>
        {c.fields.map((f) => (
          <tr key={f.field} className="border-t border-white-three">
            <td className="py-1.5 pr-4 font-mono text-xs text-gray-three">
              {f.field}
            </td>
            <td className="py-1.5 pr-4 text-gray-five">{show(f.portal)}</td>
            <td className="py-1.5 text-gray-five">{show(f.salesforce)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {c.status === 'open' ? (
      <div className="mt-3 flex gap-2">
        <button
          className={btnPrimary}
          disabled={busy}
          onClick={() => onResolve('portal')}
        >
          Keep the portal's values
        </button>
        <button
          className={btnSecondary}
          disabled={busy}
          onClick={() => onResolve('salesforce')}
        >
          Take Salesforce's values
        </button>
      </div>
    ) : (
      <div className="mt-2 text-xs text-gray-bold">
        Resolved: kept{' '}
        {c.resolution === 'portal' ? "the portal's" : "Salesforce's"} values ·{' '}
        {c.resolvedBy} · {when(c.resolvedAt)}
      </div>
    )}
  </div>
);

const OpsSync: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'open' | 'resolved'>('open');
  const { data: status } = useQuery({
    queryKey: ['opsSync', 'status'],
    queryFn: getSyncStatus,
  });
  const { data: conflicts, isPending } = useQuery({
    queryKey: ['opsSync', 'conflicts', tab],
    queryFn: () => getSyncConflicts(tab),
  });
  const resolve = useMutation({
    mutationFn: ({
      id,
      choice,
    }: {
      id: string;
      choice: 'portal' | 'salesforce';
    }) => resolveSyncConflict(id, choice),
    onSuccess: () => {
      showToast('Resolved', 'success');
      queryClient.invalidateQueries({ queryKey: ['opsSync'] });
    },
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });

  return (
    <>
      <PageHeader
        title="Salesforce sync"
        meta="Nightly, one way, Salesforce into the portal. Portal edits are kept; where both sides changed the same field, you choose."
      />
      <PageBody>
        {status && (
          <div
            className={`mb-6 rounded-xl border px-5 py-4 ${
              status.stale ||
              status.lastRun?.status === 'failed' ||
              status.lastRun?.status === 'partial'
                ? 'border-red-300 bg-red-50'
                : 'border-white-regular bg-white'
            }`}
          >
            <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                  Data as of
                </div>
                <div className="text-lg font-semibold text-gray-five">
                  {when(status.dataAsOf)}
                  {status.ageHours != null && (
                    <span className="ml-2 text-sm font-normal text-gray-bold">
                      {status.ageHours} h ago
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                  Last run
                </div>
                <div className="text-sm text-gray-five">
                  {status.lastRun ? (
                    <>
                      <Pill
                        value={status.lastRun.status}
                        tone={RUN_TONE[status.lastRun.status]}
                      />{' '}
                      {status.lastRun.mode} · {when(status.lastRun.startedAt)}
                      {status.lastRun.summary && (
                        <span className="ml-2 text-gray-bold">
                          {status.lastRun.summary}
                        </span>
                      )}
                    </>
                  ) : (
                    'No scheduled run recorded yet'
                  )}
                </div>
                {status.lastRun?.error && (
                  <div className="mt-1 max-w-2xl text-xs text-red-700">
                    {status.lastRun.error}
                  </div>
                )}
                {status.lastRun?.status === 'partial' &&
                  status.lastRun.failedObjects.length > 0 && (
                    <ul className="mt-1 max-w-2xl text-xs text-red-700">
                      {status.lastRun.failedObjects.map((f) => (
                        <li key={f.object}>
                          <b>{f.object}</b> did not refresh
                          {f.error ? ` — ${f.error}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                  Open conflicts
                </div>
                <div className="text-lg font-semibold text-gray-five">
                  {status.openConflicts}
                </div>
              </div>
            </div>
            {status.stale && (
              <div className="mt-2 text-sm text-red-700">
                The mirror has not caught up with Salesforce in over 36 hours.
                Balances and statuses here may be behind.
              </div>
            )}
          </div>
        )}

        <Card
          title="Conflicts"
          right={
            <div className="flex gap-1">
              {(['open', 'resolved'] as const).map((t) => (
                <button
                  key={t}
                  className={
                    tab === t
                      ? 'rounded-lg bg-blue-100 px-3 py-1.5 text-xs font-semibold text-blue-primary'
                      : 'rounded-lg px-3 py-1.5 text-xs text-gray-three hover:bg-grey-50'
                  }
                  onClick={() => setTab(t)}
                >
                  {t === 'open' ? 'Open' : 'Resolved'}
                </button>
              ))}
            </div>
          }
        >
          {isPending || !conflicts ? (
            <LoadingSpinner />
          ) : conflicts.length === 0 ? (
            <EmptyState
              title={
                tab === 'open'
                  ? 'Nothing to resolve'
                  : 'No resolved conflicts yet'
              }
              hint={
                tab === 'open'
                  ? 'A conflict appears only when Salesforce and the portal changed the same field on the same record.'
                  : undefined
              }
            />
          ) : (
            conflicts.map((c) => (
              <Conflict
                key={c.id}
                c={c}
                busy={resolve.isPending}
                onResolve={(choice) => resolve.mutate({ id: c.id, choice })}
              />
            ))
          )}
        </Card>

        {status && status.runs.length > 0 && (
          <Card title="Recent runs" className="mt-6">
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Started</th>
                  <th className={thClass}>Mode</th>
                  <th className={thClass}>Result</th>
                  <th className={thClass}>Summary</th>
                </tr>
              </thead>
              <tbody>
                {status.runs.map((r) => (
                  <tr key={r.id}>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {when(r.startedAt)}
                    </td>
                    <td className={tdClass}>{r.mode}</td>
                    <td className={tdClass}>
                      <Pill value={r.status} tone={RUN_TONE[r.status]} />
                    </td>
                    <td className={`${tdClass} text-gray-three`}>
                      {r.error ?? r.summary ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {status && status.recentLog.length > 0 && (
          <Card title="Per object, latest pass" className="mt-6">
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Object</th>
                  <th className={thClass}>At</th>
                  <th className={`${thClass} text-right`}>Upserted</th>
                  <th className={`${thClass} text-right`}>
                    Merged around edits
                  </th>
                  <th className={`${thClass} text-right`}>Tombstoned</th>
                  <th className={`${thClass} text-right`}>Conflicts</th>
                </tr>
              </thead>
              <tbody>
                {status.recentLog.map((l, i) => (
                  <tr key={`${l.object}-${i}`}>
                    <td className={`${tdClass} font-mono text-xs`}>
                      {l.object}
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {when(l.at)}
                    </td>
                    {l.error ? (
                      <td className={`${tdClass} text-red-700`} colSpan={4}>
                        {l.error}
                      </td>
                    ) : (
                      <>
                        <td className={`${tdClass} text-right`}>
                          {l.upserted ?? '—'}
                        </td>
                        <td className={`${tdClass} text-right`}>
                          {l.merged ?? 0}
                        </td>
                        <td className={`${tdClass} text-right`}>
                          {l.tombstoned ?? '—'}
                        </td>
                        <td className={`${tdClass} text-right`}>
                          {l.conflicts}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </PageBody>
    </>
  );
};

export default OpsSync;
